<template>
    <div ref="playerRef" class="flashcard-player" :class="{ 'is-fullscreen': isFullscreen }">
        <!-- Overview Screen -->
        <div v-if="screen === 'overview'" class="flashcard-overview">
            <div class="overview-hero">
                <div class="deck-icon-stack">
                    <div class="deck-card deck-card-3" />
                    <div class="deck-card deck-card-2" />
                    <div class="deck-card deck-card-1">
                        <i class="uil uil-layers" />
                    </div>
                </div>
                <h2 class="overview-title">{{ title }}</h2>
            </div>

            <!-- FSRS Cards Due Summary -->
            <div v-if="dueSummary" class="tw:flex tw:items-center tw:justify-center tw:gap-3 tw:mb-4">
                <span v-if="dueSummary.new > 0" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-1 tw:rounded-full tw:bg-blue-50 tw:text-blue-700 tw:text-sm tw:font-medium">
                    <i class="uil uil-plus-circle tw:text-base" />
                    {{ dueSummary.new }} new
                </span>
                <span v-if="dueSummary.learning > 0" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-1 tw:rounded-full tw:bg-orange-50 tw:text-orange-700 tw:text-sm tw:font-medium">
                    <i class="uil uil-book-open tw:text-base" />
                    {{ dueSummary.learning }} learning
                </span>
                <span v-if="dueSummary.review > 0" class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-1 tw:rounded-full tw:bg-green-50 tw:text-green-700 tw:text-sm tw:font-medium">
                    <i class="uil uil-refresh tw:text-base" />
                    {{ dueSummary.review }} to review
                </span>
            </div>

            <div class="tw:flex tw:items-center tw:justify-center tw:gap-4 tw:mb-8">
                <div class="tw:inline-flex tw:items-center tw:gap-2">
                    <i class="uil uil-layers tw:text-lg tw:text-primary" />
                    <span class="tw:text-base tw:font-medium tw:text-gray-700">{{ cardCountLabel }}</span>
                </div>
                <span class="tw:text-gray-300 tw:text-base">&middot;</span>
                <div class="tw:inline-flex tw:items-center tw:gap-2">
                    <i class="uil uil-clock tw:text-lg tw:text-primary" />
                    <span class="tw:text-base tw:font-medium tw:text-gray-700">~{{ estimatedMinutes }} min</span>
                </div>
                <span class="tw:text-gray-300 tw:text-base">&middot;</span>
                <div class="tw:inline-flex tw:items-center tw:gap-2">
                    <i class="uil uil-brain tw:text-lg tw:text-primary" />
                    <span class="tw:text-base tw:font-medium tw:text-gray-700">Self-paced</span>
                </div>
            </div>

            <div v-if="!readOnly && cards.length" class="tw:flex tw:flex-col tw:items-center tw:mb-8">
                <e-button type="is-primary" size="is-medium" class="start-button" @click="startSession">
                    <span>Start Studying</span>
                    <span class="icon is-small"><i class="uil uil-arrow-right" /></span>
                </e-button>
            </div>

            <div v-if="showCardPreview && cards.length" class="card-preview-list">
                <div class="preview-header">
                    <p class="preview-heading">Card Preview</p>
                    <span class="preview-count">{{ cardCountLabel }}</span>
                </div>
                <div class="card-preview-scroll">
                    <div v-for="(card, index) in cards" :key="index" class="card-preview-item">
                        <span class="preview-number">{{ index + 1 }}</span>
                        <span class="preview-text">{{ card.front }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Study Session Screen -->
        <div v-else-if="screen === 'study'" class="flashcard-study">
            <div class="study-header">
                <div class="study-topbar">
                    <button class="topbar-button" @click="confirmExit">
                        <i class="uil uil-times" />
                        <span>Leave the Current Session</span>
                    </button>
                    <span class="card-counter">{{ currentIndex + 1 }} / {{ activeCards.length }}</span>
                    <button class="topbar-button" @click="toggleFullscreen">
                        <i class="uil" :class="isFullscreen ? 'uil-compress-arrows' : 'uil-expand-arrows-alt'" />
                        <span>{{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}</span>
                    </button>
                </div>
                <div class="progress-bar">
                    <div
                        class="progress-fill"
                        :style="{ width: progressWidth }"
                    />
                </div>
            </div>

            <div class="card-stage">
                <transition :name="slideDirection" mode="out-in">
                    <flashcard-card
                        :key="currentIndex"
                        :front="currentCard.front"
                        :back="currentCard.back"
                        :image-url="currentCard.imageUrl || null"
                        :is-flipped="isFlipped"
                        @flip="flipCard"
                    />
                </transition>
            </div>

            <!-- Recall Rating -->
            <div class="recall-rating" :class="{ 'is-visible': isFlipped && showMemoryRating }">
                <p class="rating-prompt">How well did you know this?</p>

                <!-- FSRS Mode: 4 buttons -->
                <div v-if="isFsrsMode" class="rating-buttons">
                    <button
                        class="rating-pill rating-danger"
                        :class="{ 'is-selected': currentResponse === 1 }"
                        :aria-pressed="currentResponse === 1"
                        @click="rateCard(1)"
                    >
                        <i class="uil uil-times-circle" />
                        <span>Again</span>
                        <kbd class="rating-key">1</kbd>
                    </button>
                    <button
                        class="rating-pill rating-hard"
                        :class="{ 'is-selected': currentResponse === 2 }"
                        :aria-pressed="currentResponse === 2"
                        @click="rateCard(2)"
                    >
                        <i class="uil uil-question-circle" />
                        <span>Hard</span>
                        <kbd class="rating-key">2</kbd>
                    </button>
                    <button
                        class="rating-pill rating-success"
                        :class="{ 'is-selected': currentResponse === 3 }"
                        :aria-pressed="currentResponse === 3"
                        @click="rateCard(3)"
                    >
                        <i class="uil uil-check-circle" />
                        <span>Good</span>
                        <kbd class="rating-key">3</kbd>
                    </button>
                    <button
                        class="rating-pill rating-easy"
                        :class="{ 'is-selected': currentResponse === 4 }"
                        :aria-pressed="currentResponse === 4"
                        @click="rateCard(4)"
                    >
                        <i class="uil uil-star" />
                        <span>Easy</span>
                        <kbd class="rating-key">4</kbd>
                    </button>
                </div>

                <!-- Legacy Mode: 3 buttons -->
                <div v-else class="rating-buttons">
                    <button
                        class="rating-pill rating-danger"
                        :class="{ 'is-selected': currentResponse === 'didnt_know' }"
                        :aria-pressed="currentResponse === 'didnt_know'"
                        @click="rateCard('didnt_know')"
                    >
                        <i class="uil uil-times-circle" />
                        <span>Didn't Know</span>
                    </button>
                    <button
                        class="rating-pill rating-warning"
                        :class="{ 'is-selected': currentResponse === 'knew_some' }"
                        :aria-pressed="currentResponse === 'knew_some'"
                        @click="rateCard('knew_some')"
                    >
                        <i class="uil uil-minus-circle" />
                        <span>Knew Some</span>
                    </button>
                    <button
                        class="rating-pill rating-success"
                        :class="{ 'is-selected': currentResponse === 'knew_it' }"
                        :aria-pressed="currentResponse === 'knew_it'"
                        @click="rateCard('knew_it')"
                    >
                        <i class="uil uil-check-circle" />
                        <span>Knew It!</span>
                    </button>
                </div>

                <p class="rating-required-hint">
                    Rate this card to continue
                </p>
            </div>

            <div class="study-navigation">
                <button
                    class="nav-button"
                    :disabled="currentIndex === 0"
                    @click="previousCard"
                >
                    <i class="uil uil-arrow-left" />
                    <span>Previous</span>
                </button>
                <button
                    v-if="currentIndex < activeCards.length - 1"
                    class="nav-button"
                    :disabled="!canAdvance"
                    @click="nextCard"
                >
                    <span>Next</span>
                    <i class="uil uil-arrow-right" />
                </button>
                <e-button
                    v-else
                    type="is-primary"
                    :disabled="!allCardsViewed || !canAdvance"
                    @click="finishSession"
                >
                    Finish
                </e-button>
            </div>
        </div>

        <!-- Session Complete Screen -->
        <div v-else-if="screen === 'complete'" class="flashcard-complete has-text-centered">
            <div class="m-b-lg">
                <span class="icon is-large has-text-success">
                    <i class="uil uil-check-circle complete-icon" />
                </span>
                <h2 class="title is-4 m-t-sm">Session Complete!</h2>
                <p class="tw:text-base tw:text-gray-500 tw:mt-1">You reviewed {{ cardCountLabel }}</p>
            </div>

            <div class="stats-card">
                <div class="stats-row">
                    <div class="stat-item">
                        <p class="stat-value stat-accuracy">{{ sessionStats.accuracy }}%</p>
                        <p class="stat-label">Accuracy</p>
                    </div>
                    <div class="stat-item">
                        <p class="stat-value stat-correct">{{ sessionStats.correct }}</p>
                        <p class="stat-label">Correct</p>
                    </div>
                    <div class="stat-item">
                        <p class="stat-value stat-review">{{ sessionStats.needsReview }}</p>
                        <p class="stat-label">Needs Review</p>
                    </div>
                </div>
                <div class="results-bar">
                    <div
                        class="results-bar-correct"
                        :style="{ width: `${sessionStats.accuracy}%` }"
                    />
                    <div
                        class="results-bar-review"
                        :style="{ width: `${100 - sessionStats.accuracy}%` }"
                    />
                </div>
            </div>

            <flashcard-stats
                v-if="isFsrsMode && activityId"
                :activity-id="activityId"
            />

            <div class="complete-actions">
                <e-button type="is-light" @click="exitComplete">
                    Back to Overview
                </e-button>
                <e-button type="is-primary" @click="studyAgain">
                    <span class="icon is-small"><i class="uil uil-refresh" /></span>
                    <span>Study Again</span>
                </e-button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import FlashcardSessionState from '@/models/FlashcardSessionState.js';
import FlashcardCard from './FlashcardCard.vue';
import FlashcardStats from './FlashcardStats.vue';

const nextQueueId = ref(0);
function getNextQueueId() {
    return nextQueueId.value++;
}

const props = defineProps({
    cards: {
        type: Array,
        required: true,
    },
    title: {
        type: String,
        default: 'Flashcards',
    },
    showMemoryRating: {
        type: Boolean,
        default: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    sessionSize: {
        type: Number,
        default: null,
    },
    totalDeckCards: {
        type: Number,
        default: 0,
    },
    showCardPreview: {
        type: Boolean,
        default: false,
    },
    ratingMode: {
        type: String,
        default: 'confidence', // 'confidence' (3 buttons) or 'fsrs' (4 buttons)
    },
    cardsDue: {
        type: Number,
        default: null,
    },
    dueSummary: {
        type: Object,
        default: null,
    },
    activityId: {
        type: [Number, String],
        default: null,
    },
    onSessionStart: {
        type: Function,
        default: null,
    },
});

const emit = defineEmits(['complete', 'back', 'study-again']);

const playerRef = ref(null);
const screen = ref('overview');
const currentIndex = ref(0);
const isFlipped = ref(false);
const viewedQueueIds = ref(new Set());
const cardResponses = reactive({}); // keyed by queueId (FSRS) or index (legacy)
const requeuedBySource = reactive({}); // sourceQueueId → requeuedQueueId
const sessionNumber = ref(1);
const slideDirection = ref('slide-left');
const isFullscreen = ref(false);
const sessionQueue = ref([]); // Array of { queueId, card } entries in FSRS mode
const cardShownAt = ref(0); // timestamp when current card was displayed
const cardTimings = reactive({}); // queueId → review_time_ms
const requeueCount = ref(0);
const sessionStartTime = ref(0);

const isFsrsMode = computed(() => props.ratingMode === 'fsrs');

// When session queue is active (both modes), use it; otherwise fall back to props.cards
const activeCards = computed(() => {
    if (sessionQueue.value.length > 0) {
        return sessionQueue.value.map(entry => entry.card);
    }
    return props.cards;
});

// Unique key for the current position — stable even when queue mutates
const currentQueueId = computed(() => {
    if (sessionQueue.value.length > 0) {
        return sessionQueue.value[currentIndex.value]?.queueId ?? null;
    }
    return currentIndex.value;
});

const currentCard = computed(() => {
    return activeCards.value[currentIndex.value] || { id: null, front: '', back: '' };
});

const currentResponse = computed(() => {
    return cardResponses[currentQueueId.value] || null;
});

const allCardsViewed = computed(() => {
    return viewedQueueIds.value.size >= activeCards.value.length;
});

const canAdvance = computed(() => {
    if (props.showMemoryRating) {
        return !!currentResponse.value;
    }
    return isFlipped.value;
});

const isSessionSubset = computed(() => {
    return props.totalDeckCards > 0 && activeCards.value.length < props.totalDeckCards;
});

const uniqueCardCount = computed(() => {
    const ids = new Set(activeCards.value.map(c => c.id));
    return ids.size;
});

const hasRepeats = computed(() => {
    return uniqueCardCount.value < activeCards.value.length;
});

const cardCountLabel = computed(() => {
    const total = activeCards.value.length;
    const unique = uniqueCardCount.value;

    if (hasRepeats.value) {
        if (isSessionSubset.value) {
            return `${total} cards (${unique} unique of ${props.totalDeckCards})`;
        }
        return `${total} cards (${unique} unique)`;
    }

    if (isSessionSubset.value) {
        return `${total} cards`;
    }
    return `${total} cards`;
});

const estimatedMinutes = computed(() => {
    const secondsPerCard = 15;
    return Math.max(1, Math.round((activeCards.value.length * secondsPerCard) / 60));
});

const progressWidth = computed(() => {
    if (!activeCards.value.length) return '0%';
    return `${((currentIndex.value + 1) / activeCards.value.length) * 100}%`;
});

const sessionStats = computed(() => {
    let correct = 0;
    let needsReview = 0;

    // Deduplicate by card ID — use last response per card (handles re-queued cards in both modes)
    const lastResponsePerCard = {};
    sessionQueue.value.forEach(entry => {
        const response = cardResponses[entry.queueId];
        if (response) {
            lastResponsePerCard[entry.card.id] = response;
        }
    });

    Object.values(lastResponsePerCard).forEach(response => {
        if (isFsrsMode.value) {
            // FSRS: rating 1 (Again) = needs review, 2-4 = correct
            if (response === 1) {
                needsReview++;
            } else {
                correct++;
            }
        } else if (response === 'knew_it' || response === 'knew_some') {
            correct++;
        } else {
            needsReview++;
        }
    });

    const total = correct + needsReview;
    const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

    return { correct, needsReview, accuracy };
});

const submissionPayload = computed(() => {
    const stats = sessionStats.value;
    const ratingToConfidence = { 1: 'didnt_know', 2: 'knew_some', 3: 'knew_it', 4: 'knew_it' };

    let cardResponsesList;

    if (isFsrsMode.value) {
        // FSRS: send ALL attempts in order (including re-queued cards) for the review log
        let sequence = 0;
        cardResponsesList = [];
        sessionQueue.value.forEach(entry => {
            const response = cardResponses[entry.queueId];
            if (response) {
                cardResponsesList.push({
                    flashcard_card_id: entry.card.id,
                    rating: response,
                    confidence: ratingToConfidence[response] || 'knew_it',
                    review_time_ms: cardTimings[entry.queueId] || null,
                    is_requeue: entry.isRequeue || false,
                    sequence: sequence++,
                });
            }
        });
    } else {
        // Legacy: deduplicate by card ID (last response wins) via queue
        const lastPerCard = {};
        sessionQueue.value.forEach(entry => {
            const response = cardResponses[entry.queueId];
            if (response) {
                lastPerCard[entry.card.id] = response;
            }
        });
        cardResponsesList = Object.entries(lastPerCard).map(([cardId, response]) => ({
            flashcard_card_id: parseInt(cardId, 10),
            confidence: response,
        }));
    }

    const payload = {
        cards_viewed: viewedQueueIds.value.size,
        total_correct: stats.correct,
        total_needs_review: stats.needsReview,
        accuracy_percentage: stats.accuracy,
        session_number: sessionNumber.value,
        card_responses: cardResponsesList,
    };

    if (isFsrsMode.value) {
        payload.cards_due_at_start = props.dueSummary?.total_due ?? null;
        payload.cards_new_at_start = props.dueSummary?.new ?? null;
        payload.cards_learning_at_start = props.dueSummary?.learning ?? null;
        payload.cards_review_at_start = props.dueSummary?.review ?? null;
        payload.requeue_count = requeueCount.value;
        payload.session_duration_ms = Date.now() - sessionStartTime.value;
    }

    return payload;
});

// ── Fullscreen ──
// Uses CSS-based fullscreen (position:fixed) instead of the browser Fullscreen
// API so that PrimeVue Image preview overlays (teleported to <body>) remain
// visible above the player.

function enterFullscreen() {
    isFullscreen.value = true;
    document.body.classList.add('flashcard-fullscreen-active');
    document.body.style.overflow = 'hidden';
}

function exitFullscreen() {
    isFullscreen.value = false;
    document.body.classList.remove('flashcard-fullscreen-active');
    document.body.style.overflow = '';
}

function toggleFullscreen() {
    if (isFullscreen.value) {
        exitFullscreen();
    } else {
        enterFullscreen();
    }
}

// ── Session Actions ──

function markCurrentCardViewed() {
    const qid = currentQueueId.value;
    if (qid !== null) {
        viewedQueueIds.value.add(qid);
    }
}

function resetSessionProgress() {
    currentIndex.value = 0;
    isFlipped.value = false;
    viewedQueueIds.value = new Set();
    Object.keys(cardResponses).forEach(key => delete cardResponses[key]);
    Object.keys(cardTimings).forEach(key => delete cardTimings[key]);
    Object.keys(requeuedBySource).forEach(key => delete requeuedBySource[key]);
    sessionQueue.value = [];
    nextQueueId.value = 0;
    requeueCount.value = 0;
    cardShownAt.value = 0;
}

function initSessionQueue() {
    // Always create the queue for both modes — enables queueId-based tracking and re-queue
    sessionQueue.value = props.cards.map(card => ({
        queueId: getNextQueueId(),
        card: { ...card },
        isRequeue: false,
    }));

    sessionStartTime.value = Date.now();

    // Persist to VuexORM only in FSRS mode (model has FSRS-specific fields)
    if (isFsrsMode.value) {
        const uuid = props.activityId ? String(props.activityId) : 'default';
        FlashcardSessionState.insertOrUpdate({
            data: {
                uuid,
                queue: sessionQueue.value.map(e => e.card),
                currentIndex: 0,
                responses: {},
                viewedIndices: [],
                sessionNumber: sessionNumber.value,
                fsrsEnabled: true,
            },
        });
    }
}

const isStarting = ref(false);

async function startSession() {
    if (isStarting.value) return;
    isStarting.value = true;

    try {
        // Give the parent a chance to persist the FlashcardSession row before
        // the player transitions to the study screen. If the parent doesn't
        // provide a callback (e.g. template/preview mode), skip straight to study.
        if (typeof props.onSessionStart === 'function') {
            await props.onSessionStart();
        }

        screen.value = 'study';
        resetSessionProgress();
        initSessionQueue();
        markCurrentCardViewed();
        cardShownAt.value = Date.now();
    } finally {
        isStarting.value = false;
    }
}

function flipCard() {
    isFlipped.value = !isFlipped.value;
}

function rateCard(rating) {
    const key = currentQueueId.value;
    const previousRating = cardResponses[key];

    // Ignore if the same rating is clicked again
    if (previousRating === rating) return;

    const wasAgain =
        previousRating !== undefined && (isFsrsMode.value ? previousRating === 1 : previousRating === 'didnt_know');
    const isAgain = isFsrsMode.value ? rating === 1 : rating === 'didnt_know';

    // If switching away from "again", remove the previously re-queued card
    if (wasAgain && !isAgain && requeuedBySource[key] !== undefined) {
        const requeuedQueueId = requeuedBySource[key];
        const idx = sessionQueue.value.findIndex(e => e.queueId === requeuedQueueId);
        if (idx !== -1) {
            // Clean up any response/timing recorded for the requeued card
            delete cardResponses[requeuedQueueId];
            delete cardTimings[requeuedQueueId];
            sessionQueue.value.splice(idx, 1);
            requeueCount.value--;
        }
        delete requeuedBySource[key];
    }

    // Store response keyed by queueId (stable across queue mutations)
    cardResponses[key] = rating;

    // Capture review timing (FSRS mode only) — only on first rating
    if (previousRating === undefined && isFsrsMode.value && cardShownAt.value > 0) {
        cardTimings[key] = Date.now() - cardShownAt.value;
    }

    // Re-queue failed cards in BOTH modes so they reappear later in the session
    if (isAgain && !wasAgain && sessionQueue.value.length > 0) {
        const entry = sessionQueue.value[currentIndex.value];
        const newQueueId = getNextQueueId();
        sessionQueue.value.push({
            queueId: newQueueId,
            card: { ...entry.card },
            isRequeue: true,
        });
        requeuedBySource[key] = newQueueId;
        requeueCount.value++;
    }
}

function nextCard() {
    if (currentIndex.value < activeCards.value.length - 1) {
        slideDirection.value = 'slide-left';
        currentIndex.value++;
        isFlipped.value = false;
        markCurrentCardViewed();
        cardShownAt.value = Date.now();
    }
}

function previousCard() {
    if (currentIndex.value > 0) {
        slideDirection.value = 'slide-right';
        currentIndex.value--;
        isFlipped.value = false;
    }
}

function handleKeydown(event) {
    if (screen.value !== 'study') return;
    switch (event.key) {
        case 'ArrowLeft':
            event.preventDefault();
            previousCard();
            break;
        case 'ArrowRight':
            event.preventDefault();
            if (currentIndex.value < activeCards.value.length - 1 && canAdvance.value) {
                nextCard();
            }
            break;
        case ' ':
        case 'Spacebar':
            event.preventDefault();
            flipCard();
            break;
        case '1':
        case '2':
        case '3':
        case '4':
            if (isFsrsMode.value && isFlipped.value) {
                event.preventDefault();
                rateCard(parseInt(event.key, 10));
            }
            break;
        default:
            break;
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
    if (isFullscreen.value) {
        exitFullscreen();
    }
    document.body.classList.remove('flashcard-fullscreen-active');
});

function finishSession() {
    screen.value = 'complete';
    emit('complete', submissionPayload.value);
}

async function studyAgain() {
    if (isStarting.value) return;
    isStarting.value = true;

    try {
        sessionNumber.value++;
        resetSessionProgress();

        // Let the parent create a fresh FlashcardSession row (sessions #2+).
        if (typeof props.onSessionStart === 'function') {
            await props.onSessionStart();
        }

        screen.value = 'study';
        emit('study-again');
    } finally {
        isStarting.value = false;
    }
}

// When the parent refreshes props.cards (e.g. after loadSession resolves on study-again),
// rebuild the session queue from the fresh card data.
watch(
    () => props.cards,
    newCards => {
        if (screen.value === 'study' && newCards.length > 0 && sessionQueue.value.length === 0) {
            initSessionQueue();
            markCurrentCardViewed();
            cardShownAt.value = Date.now();
        }
    }
);

function confirmExit() {
    if (isFullscreen.value) {
        exitFullscreen();
    }
    screen.value = 'overview';
    isFlipped.value = false;
}

function exitComplete() {
    if (isFullscreen.value) {
        exitFullscreen();
    }
    screen.value = 'overview';
    emit('back');
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.flashcard-player {
    max-width: 700px;
    margin: 0 auto;
    padding: 1.5rem 1rem;

    // ── Fullscreen Mode ──
    // CSS-based fullscreen so PrimeVue Image preview overlays remain visible.
    &.is-fullscreen {
        position: fixed;
        inset: 0;
        z-index: 10000;
        max-width: 100%;
        height: 100vh;
        padding: 2rem 2rem 1.5rem;
        background: var(--scheme-main, $white);
        display: flex;
        flex-direction: column;
        overflow-y: auto;

        .flashcard-study {
            max-width: 800px;
            width: 100%;
            margin: 0 auto;
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .card-stage {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 400px;

            // Let the card fill the available width within the stage
            > * {
                width: 100%;
            }
        }

        .flashcard-complete {
            max-width: 700px;
            width: 100%;
            margin: auto;
        }

        .study-navigation {
            margin-top: auto;
            padding-top: 1.5rem;
        }

        .recall-rating {
            max-width: 800px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
        }

        .topbar-button {
            background: var(--scheme-main, $white);
            border-color: var(--border, $grey-lighter);
            color: var(--text-strong, $grey-dark);

            &:hover {
                background: var(--background, $grey-lighter);
                border-color: var(--border-hover, $grey-light);
                color: var(--text-strong, $black);
            }
        }

        .card-counter {
            color: var(--text, $grey);
        }

        .nav-button {
            color: var(--text, $grey);
            border-color: var(--border, $grey-lighter);

            &:hover:not(:disabled) {
                color: var(--text-strong, $black);
                border-color: var(--border-hover, $grey-light);
            }
        }
    }
}

// ── Overview Screen ──

.flashcard-overview {
    animation: fadeIn 0.3s ease;
}

.overview-hero {
    text-align: center;
    padding: 2rem 0 0.75rem;
}

.deck-icon-stack {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 0 auto 1.25rem;
}

.deck-card {
    position: absolute;
    border-radius: 10px;
    border: 1px solid $grey-lighter;

    &.deck-card-3 {
        width: 56px;
        height: 68px;
        background: darken($grey-lightest, 2%);
        top: 0;
        left: 50%;
        transform: translateX(-50%) rotate(6deg);
    }

    &.deck-card-2 {
        width: 56px;
        height: 68px;
        background: $grey-lightest;
        top: 3px;
        left: 50%;
        transform: translateX(-50%) rotate(3deg);
    }

    &.deck-card-1 {
        width: 56px;
        height: 68px;
        background: $white;
        top: 6px;
        left: 50%;
        transform: translateX(-50%) rotate(0deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(36, 43, 54, 0.08);

        i {
            font-size: 1.5rem;
            color: $primary;
        }
    }
}

.overview-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: $black;
    margin-bottom: 0.25rem;
    // Guard the centered hero against long / space-less deck titles so the
    // layout can never break, even if an over-long title slips past the
    // 191-char input/backend limit (belt-and-suspenders).
    max-width: 40rem;
    margin-left: auto;
    margin-right: auto;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.start-button {
    border-radius: 100px;
    padding-left: 2rem;
    padding-right: 1.75rem;
    font-weight: 500;
    min-width: 220px;

    .icon {
        margin-left: 0.25rem;
    }
}

.preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.preview-heading {
    font-size: 0.85rem;
    font-weight: 500;
    color: $grey;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.preview-count {
    font-size: 0.8rem;
    color: $grey-light;
}

.card-preview-scroll {
    max-height: 250px;
    overflow-y: auto;
    background: $white-ter;
    border-radius: $radius-large;
    padding: 0.5rem;
}

.card-preview-item {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    border-radius: $radius;
    transition: background 0.15s ease;

    &:hover {
        background: rgba($primary, 0.04);
    }
}

.preview-number {
    color: $primary;
    font-weight: 600;
    font-size: 0.85rem;
    min-width: 1.5rem;
    text-align: right;
}

.preview-text {
    color: $black;
    line-height: 1.4;
}

// ── Study Screen ──

.study-header {
    margin-bottom: 1.25rem;
    position: relative;
    z-index: 1;
}

.study-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.topbar-button {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.35rem 0.75rem;
    border: 1px solid $grey-lighter;
    border-radius: 100px;
    background: $white;
    color: $grey-dark;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    i {
        font-size: 1.1rem;
    }

    &:hover {
        background: $grey-lighter;
        color: $black;
        border-color: $grey-light;
    }
}

.card-counter {
    color: $grey;
    font-weight: 600;
    font-size: 0.9rem;
}

.progress-bar {
    width: 100%;
    height: 4px;
    background: $grey-lighter;
    border-radius: 2px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: $primary;
    border-radius: 2px;
    transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-stage {
    position: relative;
    min-height: 320px;
    // Card faces are position:absolute so they don't push flow height.
    // Pad bottom to prevent overlap with recall-rating / navigation below.
    padding-bottom: 2rem;
    margin-bottom: 1rem;
}

// ── Recall Rating ──

.recall-rating {
    position: relative;
    z-index: 1;
    padding: 1.25rem;
    background: $white-ter;
    border-radius: 12px;
    text-align: center;
    margin-top: 1rem;
    opacity: 0;
    visibility: hidden;
    transform: translateY(10px);
    transition: opacity 0.3s ease-out, visibility 0.3s ease-out, transform 0.3s ease-out;

    &.is-visible {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
    }
}

.rating-prompt {
    font-weight: 500;
    color: $grey;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
}

.rating-buttons {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.rating-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1rem;
    border: 1px solid $grey-lighter;
    border-radius: 100px;
    background: $white;
    color: $grey;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    i {
        font-size: 1.1rem;
    }

    &:hover {
        border-color: $grey-light;
    }

    &.rating-danger {
        border-left: 3px solid $danger;

        &.is-selected {
            background: $danger;
            border-color: $danger;
            color: $white;
            transform: scale(1.02);
        }
    }

    &.rating-warning {
        border-left: 3px solid $warning;

        &.is-selected {
            background: $warning;
            border-color: $warning;
            color: $black;
            transform: scale(1.02);
        }
    }

    &.rating-success {
        border-left: 3px solid $success;

        &.is-selected {
            background: $success;
            border-color: $success;
            color: $white;
            transform: scale(1.02);
        }
    }

    &.rating-hard {
        border-left: 3px solid $orange;

        &.is-selected {
            background: $orange;
            border-color: $orange;
            color: $white;
            transform: scale(1.02);
        }
    }

    &.rating-easy {
        border-left: 3px solid $info;

        &.is-selected {
            background: $info;
            border-color: $info;
            color: $white;
            transform: scale(1.02);
        }
    }
}

.rating-key {
    font-size: 0.7rem;
    padding: 0.1rem 0.35rem;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.04);
    color: inherit;
    font-family: inherit;
    line-height: 1;
    margin-left: 0.15rem;
    opacity: 0.6;
}

.rating-required-hint {
    font-size: 0.8rem;
    color: $grey-light;
    margin-top: 0.5rem;
}

// ── Navigation ──

.study-navigation {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 1.5rem;
}

.nav-button {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid $grey-lighter;
    border-radius: $radius-large;
    background: transparent;
    color: $grey;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
        font-size: 1.2rem;
    }

    &:hover:not(:disabled) {
        border-color: $grey-light;
        color: $black;
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
}

.complete-icon {
    font-size: 3rem;
}

// ── Complete Screen ──

.stats-card {
    background: $white-ter;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
}

.stats-row {
    display: flex;
    justify-content: space-around;
    margin-bottom: 1.25rem;
}

.stat-item {
    text-align: center;
}

.stat-value {
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 0.25rem;
}

.stat-accuracy {
    color: $primary;
}

.stat-correct {
    color: $success;
}

.stat-review {
    color: $danger;
}

.stat-label {
    font-size: 0.8rem;
    color: $grey;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.results-bar {
    display: flex;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
    gap: 2px;
}

.results-bar-correct {
    background: $success;
    border-radius: 3px 0 0 3px;
    transition: width 0.5s ease;
}

.results-bar-review {
    background: $danger;
    border-radius: 0 3px 3px 0;
    transition: width 0.5s ease;
}

.complete-actions {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
}

// ── Transitions ──

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition: all 0.25s ease-out;
}

.slide-left-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-left-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-right-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-right-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .flashcard-player {
        padding: 1rem 0.5rem;
    }

    .study-topbar {
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .card-counter {
        order: 3;
        width: 100%;
        text-align: center;
    }

    .topbar-button span {
        display: none;
    }

    .card-stage {
        min-height: 260px;
    }

    .recall-rating {
        padding: 1rem;
    }

    .rating-pill {
        padding: 0.4rem 0.75rem;
        font-size: 0.8rem;
    }

    .rating-key {
        display: none;
    }

    .study-navigation {
        margin-top: 1rem;
    }

    .stat-value {
        font-size: 1.5rem;
    }

    .overview-hero {
        padding: 1rem 0 0.5rem;
    }

    .flashcard-player.is-fullscreen {
        padding: 1rem;

        .card-stage {
            min-height: 280px;
        }
    }
}

@media (max-width: 576px) {
    .flashcard-player {
        padding: 0.75rem 0.25rem;
    }

    .rating-buttons {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.5rem;
    }

    .rating-pill {
        justify-content: center;
        width: 100%;
    }

    .nav-button {
        padding: 0.4rem 0.5rem;
        font-size: 0.85rem;
    }
}
</style>

<!-- Global (unscoped) style: elevate PrimeVue Image preview above the CSS-based fullscreen player -->
<style lang="scss">
body.flashcard-fullscreen-active .p-image-mask {
    z-index: 10001 !important;
}
</style>
